import { buildApiUrl, withBusinessHeaders } from './apiConfig';
import { getPortalRole, getSelectedBusinessId } from '../app/businessContext';

const PORTAL_CONTEXT_API_URL =
  process.env.REACT_APP_PORTAL_CONTEXT_API_URL || buildApiUrl('/portal-context');
const PORTAL_CONTEXT_STORAGE_PREFIX = 'seaweeds.portalContext';
const portalContextCache = new Map();

export async function fetchPortalContext({ force = false } = {}) {
  const cacheKey = getPortalContextCacheKey();
  const cachedContext = getCachedPortalContext();

  if (!force && cachedContext) {
    return cachedContext;
  }

  const request = requestPortalContext()
    .then((portalContext) => {
      portalContextCache.set(cacheKey, portalContext);
      writePersistedPortalContext(cacheKey, portalContext);
      return portalContext;
    })
    .catch((error) => {
      portalContextCache.delete(cacheKey);
      throw error;
    });

  portalContextCache.set(cacheKey, request);

  return request;
}

export function getCachedPortalContext() {
  const cacheKey = getPortalContextCacheKey();
  const cachedContext = portalContextCache.get(cacheKey);

  if (cachedContext && typeof cachedContext.then !== 'function') {
    return cachedContext;
  }

  const persistedContext = readPersistedPortalContext(cacheKey);

  if (persistedContext) {
    portalContextCache.set(cacheKey, persistedContext);
  }

  return persistedContext;
}

export function updateCachedPortalUser(user) {
  const cacheKey = getPortalContextCacheKey();
  const currentContext = getCachedPortalContext();

  if (!currentContext) {
    return;
  }

  const nextContext = {
    ...currentContext,
    user: {
      ...(currentContext.user || {}),
      ...(user || {}),
    },
  };

  portalContextCache.set(cacheKey, nextContext);
  writePersistedPortalContext(cacheKey, nextContext);
}

async function requestPortalContext() {
  let response;

  try {
    response = await fetch(PORTAL_CONTEXT_API_URL, {
      headers: withBusinessHeaders(),
    });
  } catch (error) {
    throw new Error('Unable to load business details right now.');
  }

  if (!response.ok) {
    const errorPayload = await safeReadJson(response);
    throw new Error(errorPayload?.message || 'Unable to load business details right now.');
  }

  return response.json();
}

async function safeReadJson(response) {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
}

function getPortalContextCacheKey() {
  const businessId = getSelectedBusinessId() || 'default';
  const portalRole = getPortalRole() || 'default';

  return `${PORTAL_CONTEXT_STORAGE_PREFIX}.${portalRole}.${businessId}`;
}

function readPersistedPortalContext(cacheKey) {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(cacheKey);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    window.localStorage.removeItem(cacheKey);
    return null;
  }
}

function writePersistedPortalContext(cacheKey, portalContext) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(cacheKey, JSON.stringify(portalContext));
  } catch (error) {
    // Ignore storage quota/private-mode failures; the in-memory cache still works.
  }
}
